import { useParams, Link } from 'react-router-dom';
import { articleMap, getRelatedArticles, categories } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = slug ? articleMap[slug] : undefined;

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-article-line text-2xl"></i>
          </div>
          <p className="text-foreground-600 mb-4">文章未找到</p>
          <Link
            to="/"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article, 3);
  const categoryInfo = categoryMap[article.category];
  const categoryName = categoryInfo ? categoryInfo.name : article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    url: `https://safe-guide.example.com/article/${article.slug}`,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '安全指南',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <main className="min-h-screen pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6 md:mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs font-medium text-primary-700 bg-primary-100/80 px-3 py-1 rounded-full whitespace-nowrap hover:bg-primary-200/80 transition-colors"
                >
                  {categoryName}
                </Link>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight font-heading">
                {article.title}
              </h1>

              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>

              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-secondary-700 bg-secondary-100/80 px-2.5 py-1 rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full aspect-video object-cover object-top"
                title={`${article.title} - 成人网站安全指南`}
              />
            </div>

            <div
              className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm text-foreground-500">作者：</span>
                <span className="text-sm font-medium text-foreground-800">{article.author}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-500">发布日期：</span>
                <time dateTime={article.date} className="text-sm text-foreground-600">{article.date}</time>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h3 className="text-xl md:text-2xl font-bold text-foreground-900 mb-8 font-heading">
                相关文章推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}